/* eslint-disable @typescript-eslint/no-explicit-any */
import { GetStaticPropsContext } from 'next'
import {
  getStories,
  getStoryBySlug,
  StoryblokImage,
  StoryData,
} from '@cinch-nx/data-storyblok'
import {
  ArticlesSubHubPageStoryContent,
  ArticleStoryContent,
} from '@cinch-nx/landing-ui-articles'
import { tagError, traceFunction } from '@core/datadog-server'

const instrumentedGetStoryBySlug = traceFunction(
  'getStoryBySlug',
  getStoryBySlug,
)

const instrumentedGetStories = traceFunction('getStories', getStories)

interface GetStaticPropsGeneratorArgs {
  subHubPath: string
  resultsPerPage: number
  breadCrumbs?: { text: string; url: string }[]
  generatePageBreadcrumb?: boolean
}

export function getStaticPropsGenerator({
  subHubPath,
  resultsPerPage = 10,
  breadCrumbs = [],
  generatePageBreadcrumb = false,
}: GetStaticPropsGeneratorArgs) {
  return async ({ preview = false, params }: GetStaticPropsContext) => {
    const version = preview ? 'draft' : 'published'
    const localBreadcrumbs = [...breadCrumbs]
    let currentPage = 1

    try {
      if (typeof params?.page === 'string') {
        if (params?.page === '1') {
          // Redirect [sub-hub]/page/1 to [sub-hub]/
          return {
            redirect: {
              permanent: true,
              destination: `/${subHubPath}`,
            },
          } as const
        } else {
          currentPage = Number.parseInt(params.page)
        }
      }

      const [subHubStory, { articles, total: numberOfArticles }] =
        await Promise.all([
          getSubHubStory({
            subHubPath,
            version,
          }),
          getArticlesForPage({
            subHubPath,
            version,
            perPage: resultsPerPage,
            page: currentPage,
          }),
        ])

      subHubStory.content.title =
        subHubStory.content.title || breadCrumbs[breadCrumbs.length - 1].text

      if (generatePageBreadcrumb && currentPage !== 1) {
        localBreadcrumbs.push({
          text: `Page ${currentPage}`,
          url: `/${subHubPath}/page/${currentPage}`,
        })
      }

      return {
        props: {
          preview,
          story: subHubStory,
          path: subHubPath,
          currentPage,
          numberOfArticles,
          resultsPerPage,
          articles,
          layoutProps: {
            showBorderRadius: true,
            breadCrumbs: localBreadcrumbs,
          },
          generatedAt: Date.now(),
        },
        revalidate: 60,
      }
    } catch (error) {
      console.log(error)
      if (typeof error === 'string' || error instanceof Error) {
        tagError(error)
      }
      return { notFound: true, revalidate: 60 } as const
    }
  }
}

export type StaticProps = Extract<
  Awaited<ReturnType<ReturnType<typeof getStaticPropsGenerator>>>,
  { props: any }
>['props']

function toArticle(subhubPath: string) {
  return (
    input: StoryData<ArticleStoryContent>,
  ): {
    path: string
    title: string
    image: StoryblokImage
    date: string
  } => ({
    path: `/${subhubPath}/${input.slug}`,
    title: input.content.title,
    image: input.content.image,
    date: input.published_at ?? '',
  })
}

async function getSubHubStory({
  subHubPath,
  version,
}: {
  subHubPath: string
  version: 'draft' | 'published'
}) {
  const story =
    await instrumentedGetStoryBySlug<ArticlesSubHubPageStoryContent>(
      `articles/hubs/${subHubPath}`,
      {
        version,
      },
    )

  if (
    !story ||
    !story.content ||
    story.content.component !== 'ArticlesSubHubPage'
  ) {
    throw new Error(`Failed to load story for sub-hub ${subHubPath}`)
  }

  return story
}

async function getArticlesForPage({
  subHubPath,
  version,
  page,
  perPage,
}: {
  version: 'draft' | 'published'
  subHubPath: string
  page: number
  perPage: number
}) {
  const articleStories = await instrumentedGetStories<ArticleStoryContent>({
    version,
    starts_with: `articles/${subHubPath}`,
    page,
    per_page: perPage,
    sort_by: 'published_at:desc',
  }).catch(() => ({
    stories: [],
    total: 0,
  }))

  if (articleStories.stories.length === 0) {
    throw new Error(
      `no articles available for "articles/${subHubPath}" page ${page}`,
    )
  }

  return {
    articles: articleStories.stories.map(toArticle(subHubPath)),
    total: Number(articleStories.total),
  }
}
